import { Dispatch, SetStateAction } from "react";
import { Action } from "redux";
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from "../../lib";

import {
  doDATA_DOWNLOAD_FLOW_METADATA_UPSERT,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
} from "../state";
import { getToken } from "./token_fetcher";
import { Job } from "../../shared/queue";
import {
  DataDownloadFlowJobData,
  DataDownloadFlowProgress,
  ProjectDataDownloadFlowMetadata,
} from "../../model";

export const REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH =
  "REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH";
export const doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH = createDoRequestAction(
  REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
);

export const requestProjectDataDownloadFlowMetadataFetchActionCreators = {
  doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
};

export interface RequestProjectDataDownloadFlowMetadataFetchPayload extends Action {
  payload: {
    requestId: string;
    projectId: string;
  };
  setRequestResult?: Dispatch<SetStateAction<RequestResult | null>>;
}

export interface ProjectDataDownloadFlowMetadataFetchResponsePayload {
  payload: {
    lastUpdate: ProjectDataDownloadFlowMetadata | null;
    job: Job<DataDownloadFlowJobData, DataDownloadFlowProgress> | null;
  };
}

export function* requestProjectDataDownloadFlowMetadataFetch({
  payload,
  setRequestResult,
}: RequestProjectDataDownloadFlowMetadataFetchPayload): SagaIterator {
  debug("saga*requestProjectDataDownloadFlowMetadataFetch");
  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        projectId: v.string().uuid().exist(),
      }),
    });
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const { projectId } = payload;

  const accessToken = yield call(getToken);

  const result = yield request({
    method: "post",
    url: `${API_URL}/v1/web/project-data-download-flow-metadata-fetch`,
    accessToken,
    data: {
      projectId,
    },
  });

  if (!result.success || result.error) {
    const error = result.error || new Error("request did not succeed");
    debug(error);
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
          fromPayload: payload,
          requestId: payload.requestId,
          url: "/v1/web/project-data-download-flow-metadata-fetch",
          ...result,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const lastUpdate = result.payload?.lastUpdate as
    | ProjectDataDownloadFlowMetadata
    | null
    | undefined;
  if (lastUpdate?.id) {
    yield put(doDATA_DOWNLOAD_FLOW_METADATA_UPSERT(lastUpdate));
  }

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
        fromPayload: payload,
        requestId: payload.requestId,
        url: "/v1/web/project-data-download-flow-metadata-fetch",
        ...result,
      },
      setRequestResult,
    ),
  );
}

export function* ProjectDataDownloadFlowMetadataFetchSaga() {
  yield takeEvery(
    REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
    requestProjectDataDownloadFlowMetadataFetch,
  );
}
